import { createSelector, createSlice } from "@reduxjs/toolkit";
import { RootState } from "../../../configureStore";
import {
  fetchCerealTypeDataTableDictionary,
  fetchDataColumns,
} from "../../actions/dataTable/DataTableAsyncActions";
import {
  CerealType,
  ColumnsDataTable,
  ColumnSwitch,
} from "../../actions/dataTable/DataTableTypes";
import { GridColumnVisibilityModel, GridSortModel } from "@mui/x-data-grid-pro";
import { logOut } from "../layout";

export const localeData = createSelector(
  (state: RootState) => state.LayoutReducer.locale,
  (locale) => {
    return locale;
  }
);

export const isViewerOnly = createSelector(
  (state: RootState) => state.Authentication.isViewerOnly,
  (locale) => {
    return locale;
  }
);

export type InarixGridFilterModel = {
  items: {
    field: string;
    column: string;
    operator: string;
    value: any;
    realOperator: string;
    columnType: string;
  }[];
};

export type TableOptions = {
  page: number;
  rowOptions: number;
  totalRows: number | null;
};

export type DataTableState = {
  loading: boolean;
  error: string;
  columns: ColumnsDataTable[] | undefined;
  columnSwitches: ColumnSwitch[];
  columnsVisibility: GridColumnVisibilityModel | undefined;
  columnsToExport: any[];
  columnToSearch: string;
  isRenderingSwitches: boolean;
  filtersSideBarVisible: boolean;
  filterModel: InarixGridFilterModel;

  cerealInformation: CerealType[];
  pinnedColumns: { left: string[]; right: string[] };
  columnOrder: string[];
  isOpenFilters: {
    locations: boolean;
    users: boolean;
    actions: boolean;
  };
  showFilterForm: boolean;
  preselectedFilterField: string | null;
  sortModel: GridSortModel;
};

export const initialState: DataTableState = {
  loading: false,
  error: "",
  columns: [],
  columnSwitches: [],
  columnsVisibility: undefined,
  columnsToExport: [],
  columnToSearch: "",
  isRenderingSwitches: false,
  filtersSideBarVisible: false,
  filterModel: { items: [] },

  cerealInformation: [],
  pinnedColumns: { left: [], right: [] },
  columnOrder: [],
  isOpenFilters: {
    locations: false,
    users: false,
    actions: false,
  },
  showFilterForm: false,
  preselectedFilterField: null,
  sortModel: [],
};

const CreateDataTableSlice = createSlice({
  name: "DataTableReducer",
  initialState,
  reducers: {
    setSortModel(state, action) {
      return {
        ...state,
        sortModel: action.payload,
      };
    },
    setShowFilterForm(state, action) {
      return {
        ...state,
        showFilterForm: action.payload,
      };
    },
    setPreselectedFilterField(state, action) {
      return {
        ...state,
        preselectedFilterField: action.payload,
      };
    },
    setColumnsSwitches(state, action) {
      return {
        ...state,
        columnSwitches: action.payload,
      };
    },
    setPinnedColumns(state, action) {
      return {
        ...state,
        pinnedColumns: action.payload,
      };
    },
    setIsOpenFilters(state, action) {
      return {
        ...state,
        isOpenFilters: action.payload,
      };
    },
    setColumns(state, action) {
      return {
        ...state,
        columns: action.payload,
      };
    },
    setColumnOrder(state, action) {
      return {
        ...state,
        columnOrder: action.payload,
      };
    },

    setSideBarFiltersVisible(state, action) {
      return {
        ...state,
        filtersSideBarVisible: action.payload,
      };
    },

    setFilterModel(state, action) {
      return {
        ...state,
        filterModel: action.payload,
      };
    },

    selectAllColumns(state, action) {
      const newArr = state.columnSwitches.map((el) => {
        if (el.name === action.payload) {
          return { ...el, isSelected: true };
        } else return { ...el, isSelected: true };
      });

      const columnsVisibilityModelTranform = newArr.map((col) => {
        const visibility = {
          col: col.id,
          show: true,
        };
        return visibility;
      });
      const columnsVisibilityModel = columnsVisibilityModelTranform.reduce(
        (obj, item) => Object.assign(obj, { [item.col]: true }),
        {}
      );

      return {
        ...state,
        columnsVisibility: columnsVisibilityModel,
        columnSwitches: newArr,
        columnsToExport: newArr,
      };
    },

    disableAllColumns(state, action) {
      const newArr = state.columnSwitches.map((el) => {
        if (el.name === action.payload) {
          return { ...el, isSelected: false };
        } else return { ...el, isSelected: false };
      });

      const columnsVisibilityModelTranform = newArr.map((col) => {
        const visibility = {
          col: col.id,
          show: false,
        };
        return visibility;
      });
      const columnsVisibilityModel = columnsVisibilityModelTranform.reduce(
        (obj, item) => Object.assign(obj, { [item.col]: false }),
        {}
      );

      return {
        ...state,
        columnsVisibility: columnsVisibilityModel,
        columnSwitches: newArr,
        columnsToExport: newArr,
      };
    },

    changeStatusColumn(state, action) {
      const newArr = state.columnSwitches.map((el: ColumnSwitch) => {
        if (el.id === action.payload) {
          return { ...el, isSelected: !el.isSelected };
        } else return { ...el, isSelected: el.isSelected };
      });

      const columnsVisibilityModelTranform = newArr.map((col: ColumnSwitch) => {
        const visibility = {
          col: col.id,
          show: col.isSelected,
        };
        return visibility;
      });
      const columnsVisibilityModel = columnsVisibilityModelTranform.reduce(
        (obj, item) => Object.assign(obj, { [item.col]: item.show }),
        {}
      );
      return {
        ...state,
        columnsVisibility: columnsVisibilityModel,
        columnSwitches: newArr,
        columnsToExport: newArr,
      };
    },

    setColumnsSearch(state, action) {
      return { ...state, columnToSearch: action.payload };
    },

    setColumnsDataTable(state, action) {
      return { ...state, columns: action.payload };
    },
    setColumnsVisibilitySwitchesAndExport(state, action) {
      return {
        ...state,
        columnSwitches: action.payload,
        columnsToExport: action.payload,
        columnsVisibility:
          state.columnsVisibility ??
          action?.payload?.reduce(
            (obj, item) => Object.assign(obj, { [item.field]: item.show }),
            {}
          ),
      };
    },

    applyViewConfiguration(state, action) {
      const viewColumnsVisibility = action.payload;

      // Update columnSwitches based on viewConfig.columnsVisibility
      const updatedColumnSwitches = state.columnSwitches.map((column) => ({
        ...column,
        isSelected: viewColumnsVisibility[column.id] !== false,
      }));

      return {
        ...state,
        columnSwitches: updatedColumnSwitches,
        columnsToExport: updatedColumnSwitches,
        columnsVisibility: viewColumnsVisibility,
      };
    },

    setRenderSwitches(state, actions) {
      return { ...state, isRenderingSwitches: actions.payload };
    },

    resetDataTableState() {
      return initialState;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchCerealTypeDataTableDictionary.pending, (state) => {
        state.loading = true;
        state.error = "";
      })
      .addCase(
        fetchCerealTypeDataTableDictionary.fulfilled,
        (state, action) => {
          state.loading = false;
          state.cerealInformation = action.payload;
        }
      )
      .addCase(fetchCerealTypeDataTableDictionary.rejected, (state, action) => {
        state.loading = false;
        state.cerealInformation = [];
        state.error = action.payload as string;
      });

    builder
      .addCase(fetchDataColumns.pending, (state) => {
        state.loading = true;
        state.error = "";
      })
      .addCase(fetchDataColumns.fulfilled, (state, action) => {
        state.loading = false;
        state.columns = action.payload;

        // Only set initial columnsVisibility if it doesn't exist yet

        state.columnsVisibility ??= action?.payload?.reduce(
          (obj, item) => Object.assign(obj, { [item.field]: item.show }),
          {}
        );

        // Preserve existing columnSwitches state if it exists

        if (state.columnSwitches.length === 0) {
          // Only set initial switches if none exist yet
          const initialSwitches =
            action.payload
              ?.filter((col) => !col.alwaysHide)
              .map((col) => ({
                id: col.field,
                name: col.headerName,
                isSelected: col.show,
                columnType: col.columnType,
              })) || [];

          state.columnSwitches = initialSwitches;
          state.columnsToExport = initialSwitches;
        } else {
          // Update existing switches with new column data but preserve isSelected state
          const updatedSwitches =
            action.payload
              ?.filter((col) => !col.alwaysHide)
              .map((col) => {
                const existingSwitch = state.columnSwitches.find(
                  (s) => s.id === col.field
                );
                return {
                  id: col.field,
                  name: col.headerName,
                  isSelected: existingSwitch
                    ? existingSwitch.isSelected
                    : col.show,
                  columnType: col.columnType,
                };
              }) || [];

          state.columnSwitches = updatedSwitches;
          state.columnsToExport = updatedSwitches;

          // Update columnsVisibility based on current switch states
          state.columnsVisibility = updatedSwitches.reduce(
            (obj, item) => Object.assign(obj, { [item.id]: item.isSelected }),
            {}
          );
        }
      })
      .addCase(fetchDataColumns.rejected, (state, action) => {
        state.loading = false;
        state.columns = [];
        state.error = action.payload as string;
      });

    builder.addCase(logOut, () => {
      return initialState;
    });
  },
});

export const {
  setSortModel,
  setColumnsSwitches,
  setPinnedColumns,
  setIsOpenFilters,
  setShowFilterForm,
  setPreselectedFilterField,

  setColumns,
  setColumnOrder,
  setSideBarFiltersVisible,
  setFilterModel,
  selectAllColumns,
  disableAllColumns,
  setRenderSwitches,
  setColumnsSearch,
  setColumnsDataTable,
  resetDataTableState,
  setColumnsVisibilitySwitchesAndExport,
  changeStatusColumn,
  applyViewConfiguration,
} = CreateDataTableSlice.actions;
export const DataTableReducer = CreateDataTableSlice.reducer;
